import { Link, router } from "@inertiajs/react";
import type { KeyboardEvent, MouseEvent } from "react";

import imageLanding from "@/assets/image_landing.webp";
import { CalendarIcon, PinIcon } from "@/components/icons";
import { formatAmount, formatDateTimeRange } from "@/lib/formatting";
import {
  cancelForStudentEnrollment,
  cancelForStudentWaitList,
  confirmForStudentEnrollment,
  newForStudentEnrollments,
} from "@/routes";
import type { Course } from "@/types";

export type MyEnrollment = {
  id: number;
  course_id: number;
  inscription_status: string;
};

export type MyWaitList = {
  id: number;
  course_id: number;
};

type StudentCourseCardProps = {
  course: Course;
  /** "enroll" → steel "Inscríbete aquí"; "cancel" → the user's own
   *  cancel/confirm actions (pass `enrollment` or `waitList`); "past" → a
   *  read-only status label instead of actions (the course already happened,
   *  so confirming/cancelling no longer applies). */
  variant: "enroll" | "cancel" | "past";
  /** The user's enrollment in this course (cancel/past variant). */
  enrollment?: MyEnrollment;
  /** The user's waitlist spot in this course (cancel/past variant) — shows a
   *  tag and only offers cancelling. */
  waitList?: MyWaitList;
  /** Sizing for the root element. Defaults to the fixed width `Carousel`
   *  items need to own their own size; pass a fluid className (e.g. `w-full`)
   *  for grid layouts instead. */
  className?: string;
  /** When set, the whole card navigates here on click (used by staff viewing
   *  a user's profile). Inner links/buttons keep their own behavior. */
  href?: string;
};

const DEFAULT_CARD_CLASSES =
  "flex w-[21rem] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-ink/20 bg-white shadow-sm sm:w-[30rem]";

/** Course card for the natural-user (asistente) courses view. */
export default function StudentCourseCard({
  course,
  variant,
  enrollment,
  waitList,
  className = DEFAULT_CARD_CLASSES,
  href,
}: StudentCourseCardProps) {
  const houseName = course.house_name as string | null;
  const houseAddress = course.house_address as string | null;
  const categoryName = course.category_name as string | null;
  const hasVacancies = (course.quota ?? 0) > 0;
  // The course itself is full and only taking waitlist sign-ups (distinct
  // from `waitList`, which is the current user's own waitlist spot).
  const waitlistOnly =
    variant === "enroll" && !hasVacancies && !!course.enable_waitlist;

  const clickableProps = href
    ? {
        role: "link",
        tabIndex: 0,
        onClick: (e: MouseEvent<HTMLDivElement>) => {
          if ((e.target as HTMLElement).closest("a, button")) return;
          router.visit(href);
        },
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.target === e.currentTarget && e.key === "Enter")
            router.visit(href);
        },
      }
    : {};

  return (
    <div
      className={href ? `${className} cursor-pointer` : className}
      {...clickableProps}
    >
      <div className="relative">
        <img
          src={course.house_image_url ?? imageLanding}
          alt={houseName ?? course.name ?? "Curso"}
          className="aspect-[2/1] w-full object-cover"
        />
        {course.identifier && (
          <span className="absolute bottom-2 left-2 rounded-sm bg-ink/70 px-2 py-0.5 font-sans text-[10px] font-light tracking-wide text-white uppercase">
            Código: {course.identifier}
          </span>
        )}
        {(waitList || waitlistOnly) && (
          <span className="absolute right-2 bottom-2 rounded-sm bg-olive/70 px-2 py-0.5 font-sans text-[10px] font-semibold tracking-wide text-ink uppercase">
            En lista de espera
          </span>
        )}
      </div>

      <div className="flex grow flex-wrap items-stretch justify-between gap-3 p-4">
        <div className="flex min-w-0 flex-[1_1_14rem] flex-col gap-1.5">
          <h3 className="truncate font-sans text-base font-semibold text-ink uppercase">
            {categoryName || "-"}
          </h3>

          <p className="flex items-center gap-1 font-sans text-xs font-light text-ink/80">
            <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
            {formatDateTimeRange(
              course.start_date,
              course.end_date,
              course.start_time,
              course.end_time,
            )}
          </p>

          {houseName && (
            <p className="flex items-center gap-1 font-sans text-xs font-light text-ink/80">
              <PinIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="group relative min-w-0">
                <span className="block truncate">{houseName}</span>
                {houseAddress && (
                  <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1 hidden w-max max-w-56 rounded-lg bg-ink px-2 py-1 font-sans text-[11px] font-normal text-white shadow-lg group-hover:block">
                    {houseAddress}
                  </span>
                )}
              </span>
            </p>
          )}
        </div>

        <div className="flex grow flex-col items-end justify-between gap-2">
          <p className="font-sans text-xs font-semibold text-deep">
            Aporte: {formatAmount(course.amount)}
          </p>
          {variant === "enroll" ? (
            <EnrollAction course={course} hasVacancies={hasVacancies} />
          ) : variant === "past" ? (
            <PastYear course={course} />
          ) : (
            <CancelActions enrollment={enrollment} waitList={waitList} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Full course + waitlist enabled → the button reads "Lista de espera" (the
 *  enrollment form itself switches to waitlist mode, backend decides). A
 *  full course without waitlist shows the "completo" note instead. */
function EnrollAction({
  course,
  hasVacancies,
}: {
  course: Course;
  hasVacancies: boolean;
}) {
  if (!hasVacancies && !course.enable_waitlist) {
    return (
      <p className="font-sans text-xs font-light text-ink/60 italic">
        El curso está completo.
      </p>
    );
  }

  return (
    <Link
      href={newForStudentEnrollments({ query: { course_id: course.id } }).url}
      className={
        hasVacancies ? ACTION_LINK_CLASSES.steel : ACTION_LINK_CLASSES.olive
      }
    >
      {hasVacancies ? "Inscríbete aquí" : "Lista de espera"}
    </Link>
  );
}

const ACTION_LINK_CLASSES = {
  steel:
    "rounded-full bg-steel px-4 py-1.5 font-sans text-[11px] font-semibold tracking-wide text-white uppercase transition-colors hover:bg-steel/90",
  olive:
    "rounded-full bg-olive/70 px-4 py-1.5 font-sans text-[11px] font-semibold tracking-wide text-ink uppercase transition-colors hover:bg-olive/90",
  danger:
    "rounded-full bg-deep-red px-4 py-1.5 font-sans text-[11px] font-semibold tracking-wide text-white uppercase transition-colors hover:bg-deep-red/90",
};

/** Read-only counterpart to `CancelActions` for courses that already
 *  happened — no actions apply, and pending/confirmed no longer means
 *  anything, so just surface the year (the date range above never shows
 *  it, since `formatDateRange` only adds the year when it spans two). */
function PastYear({ course }: { course: Course }) {
  if (!course.start_date) return null;

  return (
    <span className="font-sans text-xs font-semibold text-ink/60">
      {course.start_date.slice(0, 4)}
    </span>
  );
}

/** Waitlist spot → only cancel. Pending enrollment → confirm + cancel side by
 *  side. Confirmed enrollment → only cancel. */
function CancelActions({
  enrollment,
  waitList,
}: {
  enrollment?: MyEnrollment;
  waitList?: MyWaitList;
}) {
  if (waitList) {
    return (
      <Link
        href={cancelForStudentWaitList(waitList.id).url}
        className={ACTION_LINK_CLASSES.danger}
      >
        Anula tu inscripción
      </Link>
    );
  }

  if (!enrollment) return null;

  return (
    <div className="flex flex-col items-end gap-2">
      {enrollment.inscription_status === "pending" && (
        <Link
          href={confirmForStudentEnrollment(enrollment.id).url}
          className={ACTION_LINK_CLASSES.steel}
        >
          Confirmar asistencia
        </Link>
      )}
      <Link
        href={cancelForStudentEnrollment(enrollment.id).url}
        className={ACTION_LINK_CLASSES.danger}
      >
        Anular inscripción
      </Link>
    </div>
  );
}
